import { useState, type MouseEvent } from 'react';
import { Share2 } from 'lucide-react';
import { copyTextToClipboard, type ClipboardCopyResult } from '@/components/shared';

const COPY_RECORDING_TIME_LABEL = 'Copy share link for this recording time';
const SHARE_LINK_COPIED_LABEL = 'Share link copied!';
const CLIPBOARD_UNAVAILABLE_LABEL = 'Clipboard unavailable';
const COPY_FAILED_LABEL = 'Could not copy share link';
const RECORDING_TIME_PARAM = 'recordingTime';
const INTERVIEW_TAB_PARAM = 'tab';
const RECORDING_TAB = 'recording';

interface InterviewRecordingTimeShareButtonProps {
  timeSeconds: number;
  location?: RecordingTimeShareLocation | undefined;
  className?: string | undefined;
}

export type RecordingTimeShareLocation = Pick<Location, 'pathname' | 'search' | 'hash'>;

function formatRecordingTimeQueryValue(timeSeconds: number): string {
  return Number(Math.max(0, timeSeconds).toFixed(2)).toString();
}

function getTooltip(copyResult: ClipboardCopyResult | null): string {
  switch (copyResult) {
    case 'copied':
      return SHARE_LINK_COPIED_LABEL;
    case 'unsupported':
      return CLIPBOARD_UNAVAILABLE_LABEL;
    case 'error':
      return COPY_FAILED_LABEL;
    default:
      return COPY_RECORDING_TIME_LABEL;
  }
}

function shouldUseDefaultRelativePosition(className: string | undefined): boolean {
  return !/\b(absolute|fixed|sticky)\b/.test(className ?? '');
}

export function createRecordingTimeShareUrl(
  location: RecordingTimeShareLocation,
  timeSeconds: number
): string {
  const params = new URLSearchParams(location.search);
  params.set(INTERVIEW_TAB_PARAM, RECORDING_TAB);
  params.set(RECORDING_TIME_PARAM, formatRecordingTimeQueryValue(timeSeconds));
  const query = params.toString();
  return `${window.location.origin}${location.pathname}${query ? `?${query}` : ''}${location.hash}`;
}

export function InterviewRecordingTimeShareButton({
  timeSeconds,
  location,
  className,
}: InterviewRecordingTimeShareButtonProps) {
  const [copyResult, setCopyResult] = useState<ClipboardCopyResult | null>(null);
  const tooltip = getTooltip(copyResult);
  const disabled = !Number.isFinite(timeSeconds) || timeSeconds < 0;

  async function handleCopy(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();

    if (disabled) {
      return;
    }

    const shareUrl = createRecordingTimeShareUrl(location ?? window.location, timeSeconds);
    setCopyResult(await copyTextToClipboard(shareUrl));
  }

  return (
    <div
      className={[
        'group/recording-share',
        shouldUseDefaultRelativePosition(className) ? 'relative' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseLeave={() => setCopyResult(null)}
      onBlur={() => setCopyResult(null)}
    >
      <button
        type="button"
        className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
        aria-label={COPY_RECORDING_TIME_LABEL}
        title={tooltip}
        disabled={disabled}
        onClick={(event) => {
          void handleCopy(event);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 hidden whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white group-hover/recording-share:block group-focus-within/recording-share:block">
        {tooltip}
      </span>
    </div>
  );
}
